/* eslint-disable no-await-in-loop */
import { NOT_FOUND_PATH } from '@helpers/constants';
// eslint-disable-next-line import/no-cycle
import {
    getChildRoutes,
    getDefinitionFromPath,
    PROTECTED_ROUTES,
    RouteDefinition,
} from '@helpers/routeDefinitions';
import useUser from '@hooks/useUser';
import useUserPermission from '@hooks/useUserPermission';
import defaultHomePage from '@stateAtoms/defaultHomePage';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function useTryNavigate() {
    const navigate = useNavigate();
    const { featuresLoadedAndIsAllowedAsync } = useUserPermission();
    const [allowedHomePage, setAllowedHomePage] = useAtom(defaultHomePage);
    const user = useUser();

    useEffect(() => {
        void getAllowedHomePage();
    }, [user?.features]);

    async function getAllowedHomePage() {
        const page = await tryGetPath(allowedHomePage);

        if (page !== allowedHomePage) setAllowedHomePage(page);
    }

    const tryNavigateAsync = useCallback(
        async (path: string) => {
            const allowedPath = await tryGetPath(path);

            navigate(allowedPath);
        },
        [featuresLoadedAndIsAllowedAsync],
    );

    const tryGetPath = useCallback(
        async (path: string) => {
            const route = getDefinitionFromPath(path);

            if (
                await featuresLoadedAndIsAllowedAsync(
                    path,
                    route.profilePermission,
                    route.featurePermission,
                )
            ) {
                return path;
            }

            const firstAllowed = await getFirstAllowedRoute();

            return firstAllowed ?? NOT_FOUND_PATH;
        },
        [featuresLoadedAndIsAllowedAsync],
    );

    const getFirstAllowedRoute = useCallback(
        async (
            root: RouteDefinition = PROTECTED_ROUTES,
        ): Promise<string | null> => {
            const routes = getChildRoutes(root);

            for (const r of routes) {
                const allowed = await featuresLoadedAndIsAllowedAsync(
                    r.path,
                    r.profilePermission,
                    r.featurePermission,
                );

                if (allowed) {
                    return r.path ?? null;
                }
            }

            return NOT_FOUND_PATH;
        },
        [featuresLoadedAndIsAllowedAsync],
    );

    return { tryNavigateAsync, allowedHomePage };
}
